/**
 * Centralized settings storage + helpers for the ADN PWA.
 * Everything persists to localStorage and emits `adn-settings-change` so any
 * mounted component can react without prop drilling.
 *
 * Keys are namespaced `adn_*` so they're easy to grep and audit.
 */
import { useEffect, useState } from "react";

export const SETTINGS_EVENT = "adn-settings-change";

// All known setting keys + their defaults. The Settings page reads/writes these.
export const SETTINGS_DEFAULTS = {
  // Language
  adn_lang_autodetect: "1",          // "1" auto-detect on first visit, "0" lock to chosen
  // AI Concierge (most already exist scattered across SupportChat — surfaced here)
  adn_chat_autospeak: "0",            // read replies aloud
  adn_chat_tts_rate: "1",             // 1, 1.25, 1.5
  adn_chat_simple_mode: "0",          // plain-language mode
  adn_chat_email_transcripts: "0",    // send transcript on chat end
  // Display & accessibility
  adn_reduce_motion: "0",             // disable animations / fades
  adn_larger_text: "0",               // increase base font size
  // Notifications
  adn_browser_notifications: "0",     // future push (placeholder)
  // PWA install hint
  adn_install_dismissed: "",          // timestamp when last dismissed
};

export function getSetting(key) {
  try {
    const v = localStorage.getItem(key);
    return v !== null ? v : SETTINGS_DEFAULTS[key] ?? "";
  } catch {
    return SETTINGS_DEFAULTS[key] ?? "";
  }
}

export function setSetting(key, value) {
  try {
    localStorage.setItem(key, String(value));
    window.dispatchEvent(
      new CustomEvent(SETTINGS_EVENT, { detail: { key, value: String(value) } }),
    );
  } catch {
    /* ignore */
  }
}

export function toggleBool(key) {
  const cur = getSetting(key) === "1";
  setSetting(key, cur ? "0" : "1");
  return !cur;
}

/**
 * React hook — reactive setting value. Re-renders when the value changes
 * anywhere in the app (cross-component sync).
 */
export function useSetting(key) {
  const [value, setValue] = useState(() => getSetting(key));
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.key === key) setValue(e.detail.value);
    };
    window.addEventListener(SETTINGS_EVENT, handler);
    // Also listen for cross-tab storage events
    const storageHandler = (e) => {
      if (e.key === key) setValue(e.newValue ?? SETTINGS_DEFAULTS[key] ?? "");
    };
    window.addEventListener("storage", storageHandler);
    return () => {
      window.removeEventListener(SETTINGS_EVENT, handler);
      window.removeEventListener("storage", storageHandler);
    };
  }, [key]);
  return [value, (v) => setSetting(key, v)];
}

/**
 * Applies "reduce motion" and "larger text" class hooks to <html>. Called once
 * from index.js so the classes are present before first paint.
 */
export function applyAccessibilityClasses() {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  const apply = () => {
    html.classList.toggle("adn-reduce-motion", getSetting("adn_reduce_motion") === "1");
    html.classList.toggle("adn-larger-text", getSetting("adn_larger_text") === "1");
  };
  apply();
  window.addEventListener(SETTINGS_EVENT, (e) => {
    if (e.detail?.key === "adn_reduce_motion" || e.detail?.key === "adn_larger_text") {
      apply();
    }
  });
}

/**
 * Hard-refresh the PWA: clear all caches, unregister all service workers, and
 * reload. Used by the Settings → "Check for updates" button.
 */
export async function forceAppRefresh() {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* non-fatal */
  }
  // Tiny delay so toast can render
  setTimeout(() => window.location.reload(), 250);
}

/** Returns whether the app is running as an installed PWA (standalone). */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    (window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}

export const APP_VERSION = "1.2.0";
